import React from 'react';
import { Link } from 'react-router-dom';
import { useTierPortfolio } from '../../hooks/useTierPortfolio';
import DeployableCapitalBar, {
  DeployableSegment,
} from '../../components/portfolio/DeployableCapitalBar';
import { TIER_CATALOG } from '../../tiers/tierCatalog';
import { fmtUsd } from '../../components/portfolio/format';
import { accountDetailPath } from '../../utils/appRoutes';

const DashboardTierHero: React.FC = () => {
  const { summary, accounts, accountContext } = useTierPortfolio();

  if (!summary) return null;

  const segments: DeployableSegment[] = summary.tiers
    .filter((t) => t.tierBudgetUsd > 0 || t.anyEnabled)
    .map((t) => ({
      key: String(t.riskTier),
      label: TIER_CATALOG[t.riskTier].shortName,
      usd: t.tierDeployedUsd,
      className: TIER_CATALOG[t.riskTier].railClass,
    }));

  const cashReserve = Math.max(
    0,
    summary.totalNavUsd - summary.totalDeployableUsd,
  );

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 bg-slate-950 px-5 py-4 text-white">
        <h2 className="text-lg font-semibold">Deployable capital by tier</h2>
        <p className="mt-1 text-sm text-slate-300">
          {summary.accountCount} account{summary.accountCount !== 1 ? 's' : ''} ·
          NAV {fmtUsd(summary.totalNavUsd)} · deployable{' '}
          {fmtUsd(summary.totalDeployableUsd)}
        </p>
      </div>
      <div className="space-y-4 p-5">
        <DeployableCapitalBar
          navUsd={summary.totalNavUsd}
          deployableUsd={summary.totalDeployableUsd}
          segments={segments.length ? segments : summary.tiers.map((t) => ({
            key: String(t.riskTier),
            label: TIER_CATALOG[t.riskTier].shortName,
            usd: 0,
            className: TIER_CATALOG[t.riskTier].railClass,
          }))}
          cashReserveUsd={cashReserve}
        />
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {accounts.map((account) => {
            const ctx =
              summary.accounts.find((a) => a.accountId === account.id) ??
              (accountContext?.accountId === account.id ? accountContext : null);
            const enabledTiers =
              ctx?.tiers.filter((t) => t.enabled).length ?? 0;
            return (
              <Link
                key={account.id}
                to={accountDetailPath(account.id)}
                className="rounded-lg border border-gray-200 p-3 hover:border-primary-300 hover:shadow-sm"
              >
                <p className="font-semibold text-gray-900">{account.nickname}</p>
                <p className="text-xs text-gray-500">
                  {enabledTiers} enabled tier{enabledTiers !== 1 ? 's' : ''}
                </p>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default DashboardTierHero;
